import { useState, useCallback, useMemo, useImperativeHandle, forwardRef } from "react";
import { useKeyboard } from "@/v2/shared/hooks/useKeyboard";
import { useSnippets, useCreateSnippet, useSnippetById } from "@/v2/entities/KnowledgeSnippet";
import { CreateSnippetDialog } from "@/v2/features/knowledge-management/parts/CreateSnippetDialog";
import { SnippetDetail } from "@/v2/features/knowledge-management/parts/SnippetDetail";
import { Dialog } from "@/components/shared/dialog";
import dialogStyles from "../../styles/CreateSnippetDialog.module.scss";

export interface SnippetCreationManagerRef {
  openCreateDialog: () => void;
}

export const SnippetCreationManager = forwardRef<SnippetCreationManagerRef>((_, ref) => {
  const [createSnippetDialogOpen, setCreateSnippetDialogOpen] = useState(false);
  const [snippetEditorDialogOpen, setSnippetEditorDialogOpen] = useState(false);
  const [createdSnippetId, setCreatedSnippetId] = useState<string | null>(null);

  const { data: snippets } = useSnippets();
  const { mutateAsync: createSnippet } = useCreateSnippet();
  const {
    data: snippetDetail,
    isLoading: isSnippetLoading,
    isError: isSnippetError,
    refetch: refetchSnippet,
  } = useSnippetById(createdSnippetId);

  const collectionPaths = useMemo(() => {
    if (!snippets || snippets.length === 0) {
      return [""];
    }

    const paths = new Set<string>([""]);

    snippets.forEach((snippet) => {
      const cleanPath = snippet.path.startsWith("/")
        ? snippet.path.slice(1)
        : snippet.path;
      if (!cleanPath) {
        return;
      }
      const parts = cleanPath.split("/");
      let current = "";
      parts.forEach((part) => {
        current = current ? `${current}/${part}` : part;
        paths.add(current);
      });
    });

    return Array.from(paths).sort((a, b) => {
      if (a === b) return 0;
      if (a === "") return -1;
      if (b === "") return 1;
      return a.localeCompare(b);
    });
  }, [snippets]);

  const handleCreateSnippet = useCallback(
    async (
      collectionPath: string,
      snippetName: string,
      isInternal: boolean
    ): Promise<void> => {
      const normalizedPath = collectionPath.startsWith("/")
        ? collectionPath.slice(1)
        : collectionPath;
      const response = await createSnippet({
        path: normalizedPath,
        title: snippetName,
        isInternal,
      });
      setCreatedSnippetId(response.id);
      setCreateSnippetDialogOpen(false);
      setSnippetEditorDialogOpen(true);
    },
    [createSnippet]
  );

  const handleRefreshSnippet = useCallback(async () => {
    if (createdSnippetId) {
      await refetchSnippet();
    }
  }, [createdSnippetId, refetchSnippet]);

  const openCreateDialog = useCallback(() => {
    setCreateSnippetDialogOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({
    openCreateDialog,
  }));

  useKeyboard(
    (e) => {
      if (
        (e.target instanceof HTMLInputElement ||
          e.target instanceof HTMLTextAreaElement) &&
        (e.target as HTMLElement).isContentEditable !== true
      ) {
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        setCreateSnippetDialogOpen(true);
      }
    },
    [77],
    {
      event: "keydown",
      stopPropagation: false,
      preventDefault: false,
      disableGlobalEvent: false,
    }
  );

  return (
    <>
      <CreateSnippetDialog
        open={createSnippetDialogOpen}
        onOpenChange={setCreateSnippetDialogOpen}
        defaultCollectionPath=""
        collectionPaths={collectionPaths}
        onCreateSnippet={handleCreateSnippet}
      />
      <Dialog
        open={snippetEditorDialogOpen}
        onOpenChange={(open) => {
          setSnippetEditorDialogOpen(open);
          if (!open) {
            setCreatedSnippetId(null);
          }
        }}
      >
        <Dialog.Content size="large" className={dialogStyles.snippetDialog}>
          <Dialog.Close className={dialogStyles.closeButton} />
          {createdSnippetId && (
            <SnippetDetail
              snippetDetail={snippetDetail ?? undefined}
              isLoading={isSnippetLoading}
              isError={isSnippetError}
              selectedVersionId={null}
              onSelectVersion={() => {}}
              onRefreshSnippet={handleRefreshSnippet}
            />
          )}
        </Dialog.Content>
      </Dialog>
    </>
  );
});

